import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {addWeeks, startOfWeek} from 'date-fns'
import {useToast} from '../../../../_fds/layout/components/toast-messages/ToastMessages'
import {BaseEmployeeModel} from '../core/_models'
import {
  listEmployeeExceptions,
  listLocationAvailability,
  replaceEmployeeAvailability,
  replaceEmployeeExceptions,
} from '../core/_requests'
import {
  BoardEntry,
  boardColumns,
  buildBoardRow,
  GridSlot,
  gridToExceptions,
  groupExceptionsByEmployee,
  groupWeeklyByEmployee,
  ISO_DAY_LABELS,
  replaceDaySlots,
  toDateKey,
  toWeeklySlots,
  weekDatesFrom,
} from '../helpers/availability'
import {comparePositions, getPositionDisplayName} from '../helpers/generals'
import {useStoreSelector} from '../store'
import {AvailabilityBoard, BoardRow} from './AvailabilityBoard'
import {AvailabilityCellModal} from './AvailabilityCellModal'
import {markAvailabilityChanged} from './markAvailabilityChanged'

const EMPTY_ENTRIES: GridSlot[] = []

/**
 * The roster-wide availability editor: everyone down the side, the week across
 * the top, one day per cell — the shape of the weekly schedule, so a manager
 * can read the two together.
 *
 * It is the board half of the page; the per-employee paint grid
 * (`AvailabilityPage`) is the other, and the layout toggle lives above both.
 * The split is deliberate rather than a migration: painting half hours is the
 * right tool for one person's whole week, and a list per day is the right tool
 * for a whole roster, where 48 half-hour cells per employee would be thousands
 * of controls and a drag across rows means nothing.
 *
 * Saving is per cell and immediate — a modal that saves on OK — so this page
 * has no dirty state to protect, unlike the paint grid.
 */
export const AvailabilityBoardPage = ({layoutToggle}: {layoutToggle?: ReactNode}) => {
  const {showToast} = useToast()

  const {selectedLocationId, employees, hourFormat, week} = useStoreSelector((store) => ({
    selectedLocationId: store.selectedLocationId,
    employees: store.employees,
    hourFormat: store.hourFormat,
    week: store.week,
  }))

  // 'weekly' is the standing pattern — columns are weekdays and stand for
  // every such day. 'dates' is one real week, where a column is a date and the
  // pattern shows through faded wherever the date says nothing.
  const [mode, setMode] = useState<'weekly' | 'dates'>('weekly')
  // Weeks forward from the current one. Never negative — the past cannot be
  // edited (the API refuses it too), and there is nothing to plan there.
  const [weekOffset, setWeekOffset] = useState(0)
  const [weeklyByEmployee, setWeeklyByEmployee] = useState<Map<number, GridSlot[]>>(new Map())
  const [datedByEmployee, setDatedByEmployee] = useState<Map<number, GridSlot[]>>(new Map())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  // Which cell the modal is editing, as (employee, column).
  const [editing, setEditing] = useState<{employeeId: number; column: number} | null>(null)

  // The visible week, in column order, starting on the company's configured
  // first day — so the columns need not begin on a Monday. Dateless weekly
  // mode uses it for nothing but the column ORDER (see boardColumns).
  const weekDates = useMemo(() => {
    const start = startOfWeek(addWeeks(new Date(), weekOffset), {
      weekStartsOn: week?.weekStartOn ?? 1,
    })
    return weekDatesFrom(start)
  }, [weekOffset, week?.weekStartOn])

  const columns = useMemo(
    () => boardColumns(week?.weekStartOn ?? 1, mode === 'dates' ? weekDates : undefined),
    [week?.weekStartOn, mode, weekDates]
  )

  // Leavers are excluded, not just the hidden. The store's roster is fetched
  // with include_inactive so old schedules can still name whoever worked them,
  // so an inactive employee reaches here unless it is said otherwise — and
  // availability is a statement about future weeks, which someone who has left
  // has none of. Same test the paint grid's picker and the assignee picker use.
  const rows: BoardRow[] = useMemo(
    () =>
      ((employees || []) as BaseEmployeeModel[])
        .filter((employee) => employee.is_active !== false && employee.hide_in_schedule !== true)
        .map((employee) => ({
          employeeId: employee.id,
          name: `${employee.first_name} ${employee.last_name}`.trim(),
          position: getPositionDisplayName(employee.position),
          rawPosition: employee.position,
        }))
        // Position order from the data-driven registry (sort_order), then by
        // name — the same ordering the schedule board falls back to, so the two
        // views list people in the same order.
        .sort(
          (a, b) => comparePositions(a.rawPosition, b.rawPosition) || a.name.localeCompare(b.name)
        )
        .map(({employeeId, name, position}) => ({employeeId, name, position})),
    [employees]
  )

  // Dropping a response that a newer selection has already superseded. A
  // counter rather than a cancelled flag per effect run: the same loader is
  // called again after every save, outside any effect.
  const requestRef = useRef(0)

  const load = useCallback(async () => {
    if (!selectedLocationId) {
      setWeeklyByEmployee(new Map())
      setDatedByEmployee(new Map())
      return
    }
    const request = ++requestRef.current
    setLoading(true)

    // The weekly pattern is fetched either way: it IS the board in weekly
    // mode, and the faded layer under the dates in the other.
    const weekly = await listLocationAvailability(selectedLocationId, true)
    // Employee omitted on purpose — the endpoint then answers for the whole
    // location, which is exactly one request per visible week.
    const exceptions =
      mode === 'dates'
        ? await listEmployeeExceptions(
            selectedLocationId,
            null,
            toDateKey(weekDates[0]),
            toDateKey(weekDates[6])
          )
        : undefined

    // A location/mode/week switch mid-flight must not overwrite the newer
    // selection's board with this response.
    if (request !== requestRef.current) return
    setWeeklyByEmployee(groupWeeklyByEmployee(weekly || []))
    setDatedByEmployee(
      exceptions ? groupExceptionsByEmployee(exceptions, weekDates) : new Map<number, GridSlot[]>()
    )
    setLoading(false)
  }, [selectedLocationId, mode, weekDates])

  useEffect(() => {
    load()
  }, [load])

  const entries = useMemo(() => {
    const out = new Map<number, BoardEntry[][]>()
    rows.forEach((row) =>
      out.set(
        row.employeeId,
        buildBoardRow(
          columns,
          weeklyByEmployee.get(row.employeeId) || EMPTY_ENTRIES,
          mode === 'dates' ? datedByEmployee.get(row.employeeId) || EMPTY_ENTRIES : undefined
        )
      )
    )
    return out
  }, [rows, columns, weeklyByEmployee, datedByEmployee, mode])

  const editingRow = editing ? rows.find((row) => row.employeeId === editing.employeeId) : undefined
  const editingColumn = editing ? columns[editing.column] : undefined
  const editingEntries = editing ? entries.get(editing.employeeId)?.[editing.column] ?? [] : []
  // The cell's own entries are what the modal edits; the faded ones belong to
  // the weekly column and are shown there as read-only context.
  const ownSlots = useMemo(
    () => editingEntries.filter((entry) => !entry.inherited).map((entry) => entry.slot),
    [editingEntries]
  )
  const inheritedSlots = useMemo(
    () => editingEntries.filter((entry) => entry.inherited).map((entry) => entry.slot),
    [editingEntries]
  )

  const handleSave = async (daySlots: GridSlot[]) => {
    if (!editing || !selectedLocationId || !editingColumn) return
    const {employeeId, column} = editing
    setSaving(true)
    try {
      if (mode === 'dates') {
        const dateKey = toDateKey(weekDates[column])
        await replaceEmployeeExceptions(employeeId, {
          location_id: selectedLocationId,
          // A one-day window: the save replaces exactly the date that was open
          // in the modal and leaves the rest of the week — and history — alone.
          date_from: dateKey,
          date_to: dateKey,
          slots: gridToExceptions(
            daySlots.map((slot) => ({...slot, day_of_week: column})),
            weekDates
          ),
        })
      } else {
        // The weekly endpoint is a full replace, so the other six days go back
        // with it, untouched.
        const all = replaceDaySlots(
          weeklyByEmployee.get(employeeId) || [],
          editingColumn.isoDay,
          daySlots
        )
        await replaceEmployeeAvailability(employeeId, {
          location_id: selectedLocationId,
          slots: toWeeklySlots(all),
        })
      }
      setEditing(null)
      markAvailabilityChanged()
      // Refetched rather than patched from the modal: the server re-normalizes
      // what it is sent, so this is the only version guaranteed to match the DB.
      await load()
      showToast('Availability', 'Availability saved.', 'success')
    } catch (error) {
      showToast('Availability', 'Could not save availability.', 'danger')
    } finally {
      setSaving(false)
    }
  }

  const noEmployees = !rows.length
  const dated = mode === 'dates'

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-semibold mb-1'>Availability</h3>
          <span className='text-gray-500 fs-7'>
            {dated
              ? 'One-time availability for a single week. Faded entries are the weekly pattern, still in force where a date says nothing.'
              : 'A repeating weekly pattern — it applies to every week, not a specific date. Anything not listed counts as available.'}
          </span>
        </div>
        <div className='card-toolbar'>
          <div className='d-flex align-items-center gap-2'>
            {layoutToggle}
            <button
              type='button'
              className={`btn btn-sm ${
                dated ? 'btn-primary' : 'btn-light-primary'
              } d-inline-flex align-items-center`}
              aria-pressed={dated}
              data-testid='availability-board-mode-toggle'
              disabled={loading || saving}
              title={
                dated
                  ? 'Back to the standing weekly pattern'
                  : 'Set availability for specific dates instead'
              }
              onClick={() => {
                setMode(dated ? 'weekly' : 'dates')
                setWeekOffset(0)
                setEditing(null)
              }}
            >
              <i className='bi bi-calendar-event fs-8 me-2' aria-hidden='true' />
              {dated ? 'Weekly pattern' : 'One-time availability'}
            </button>
          </div>
        </div>
      </div>

      <div className='card-body pt-4'>
        {noEmployees ? (
          <div className='text-center text-gray-500 py-10'>No team members at this location.</div>
        ) : (
          <>
            {dated && (
              <div className='d-flex align-items-center justify-content-center gap-3 mb-4'>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light'
                  data-testid='availability-board-week-prev'
                  // Nothing to plan in the past, and the API refuses to write
                  // there — so the current week is the floor rather than a step
                  // that fails on save.
                  disabled={loading || saving || weekOffset === 0}
                  aria-label='Previous week'
                  title={weekOffset === 0 ? 'This is the current week' : 'Previous week'}
                  onClick={() => setWeekOffset((offset) => Math.max(0, offset - 1))}
                >
                  <i className='bi bi-chevron-left' aria-hidden='true' />
                </button>
                <span
                  className='fw-bold text-gray-800 text-nowrap'
                  data-testid='availability-board-week-label'
                >
                  {weekDates[0].toLocaleDateString(undefined, {day: 'numeric', month: 'short'})} –{' '}
                  {weekDates[6].toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                  {weekOffset === 0 && (
                    <span className='badge badge-light-primary ms-3'>This week</span>
                  )}
                </span>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light'
                  data-testid='availability-board-week-next'
                  disabled={loading || saving}
                  aria-label='Next week'
                  title='Next week'
                  onClick={() => setWeekOffset((offset) => offset + 1)}
                >
                  <i className='bi bi-chevron-right' aria-hidden='true' />
                </button>
              </div>
            )}

            {loading && <div className='text-gray-500 fs-7 mb-2'>Loading availability…</div>}

            <AvailabilityBoard
              columns={columns}
              rows={rows}
              entries={entries}
              use12Hour={hourFormat === '12'}
              disabled={loading || saving}
              onCellClick={(employeeId, column) => setEditing({employeeId, column})}
            />

            <div className='fs-8 text-gray-500 mt-2'>
              Click a day to add, change or clear what is registered for it.
              {dated && ' Faded entries come from the weekly pattern — edit those on the pattern.'}
            </div>
          </>
        )}
      </div>

      <AvailabilityCellModal
        show={!!editing}
        title={
          editingRow && editingColumn
            ? `${editingRow.name} — ${
                editingColumn.date
                  ? editingColumn.date.toLocaleDateString(undefined, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'short',
                    })
                  : `every ${ISO_DAY_LABELS[editingColumn.isoDay]}`
              }`
            : ''
        }
        dated={dated}
        entries={ownSlots}
        inherited={inheritedSlots}
        use12Hour={hourFormat === '12'}
        saving={saving}
        onCancel={() => setEditing(null)}
        onSave={handleSave}
      />
    </div>
  )
}
